import { Text } from '@react-pdf/renderer';
import { isSameDay } from 'date-fns';
import { styles } from '.';
import type { DerivedRoleRoster, DerivedTeamRoster } from '../../hooks/types';
import { militaryTimeString } from '../../lib/utils/date-time';
import { colours, roleGroupPriorities } from '../Roster/lib/prioritised-roles';
import type { ShiftPeriod } from '../Roster/types';
import type { Division, Role, Roster, Shift } from './types';

/**
 * The maximum number of shifts in a stack of roles in the PDF.
 * The reason we have to set this is because react-pdf doesn't support column breaking,
 * so we have to hard-code the number of shifts that could fit in a column.
 */
const maxShiftsPerColumn = 15;

/**
 * Division colours that can be stacked together.
 * These colours have been chosen according to the observable division relationships
 * in the RFS org chart.
 */
const coloursThatCanBeStacked: readonly (readonly [string, string])[] = [
  [colours.black.colour, colours.yellowgreen.colour],
  [colours.darkblue.colour, colours.silver.colour],
];

/*
 * These are the roles that sit in the executive box in the PDF.
 * Based on the RFS Org Chart.
 */
const executives: readonly string[] = [
  'Safety Advisor',
  'Deputy Incident Controller',
  'Incident Controller',
  'Management Support Officer',
  'Investigations Officer',
  'Fire Investigation',
  'Bushfire Impact Analysis',
];

/*
 * These are the roles that get a highlighted role box in the PDF.
 * Based on the RFS Org Chart.
 */
const leaders: readonly string[] = [
  ...executives,
  'Rural Liaison Officer',
  'Public Liaison Officer',
  'Logistics Officer',
  'Planning Officer',
  'Intelligence Officer',
  'Finance Officer',
  'Operations Officer',
  'Air Operations Officer',
  'Divisional Officer',
  'Divisional Commander',
  'Operations Communications',
];

const isExecutive = (roleName: string): boolean =>
  executives.includes(roleName);

const isLeader = (roleName: string): boolean => leaders.includes(roleName);

/**
 * Takes an array of Roles, finds the associated roleGroup of the role from within roleGroupPriorities,
 * and returns an array of divisions, which is each role group with the roles within that group.
 *
 * Filter out any roles with no shifts scheduled
 *
 * @param roles - Array of Roles to be grouped.
 * @returns Array of objects with each role group and the roles within that group.
 */
const groupRolesByDivision = (
  roles: readonly Role[],
  thisDay: number
): readonly Division[] => {
  // The roles we know the group of
  const roleGroups: readonly Division[] = roleGroupPriorities.map(
    ({ roleGroup, colour }) => ({
      title: roleGroup,
      roles: roles.filter(({ group }) => group === roleGroup),
      colour,
    })
  );

  // The roles we don't know the group of
  const otherRoles: readonly Division[] = [
    {
      title: 'Other',
      roles: roles.filter(
        ({ title }) =>
          !roleGroups.some(({ roles }) =>
            roles.some((role) => role.title === title)
          )
      ),
      colour: colours.black.colour,
    },
  ];

  // Filter out any roles with no shifts scheduled
  return [...roleGroups, ...otherRoles]
    .map((division) => ({
      ...division,
      roles: division.roles
        .map(onlyShiftsFromThisDay(thisDay))
        .filter(({ shifts }) => shifts.length > 0),
    }))
    .filter(({ roles }) => roles.length > 0);
};

const roleRosterToRole = (roleRoster: DerivedRoleRoster): Role => ({
  title: roleRoster.roleName,
  group: roleRoster.roleGroup,
  shifts: roleRoster.users.flatMap((user) =>
    user.shifts.map(
      (shift): Shift => ({
        name: user.firstName + ' ' + user.lastName,
        startTime: new Date(shift.startTime).valueOf(),
        endTime: new Date(shift.endTime).valueOf(),
      })
    )
  ),
  isLeader: isLeader(roleRoster.roleName),
});

const formatShiftTime = (
  shiftPeriod: ShiftPeriod | undefined
): readonly (string | JSX.Element)[] =>
  shiftPeriod === undefined
    ? []
    : [
        `Day Shift:   ${militaryTimeString(
          shiftPeriod.start
        )}-${militaryTimeString(shiftPeriod.end)}`,
        <Text key={'night-shift'} style={styles.nightShift}>
          {`Night Shift: ${militaryTimeString(
            shiftPeriod.end
          )}-${militaryTimeString(shiftPeriod.start)}`}{' '}
        </Text>,
      ];

const onlyShiftsFromThisDay =
  (thisDay: number) =>
  (role: Role): Role => ({
    ...role,
    shifts: role.shifts
      // Filter out shifts that aren't on today
      .filter(
        ({ startTime, endTime }) =>
          isSameDay(startTime, thisDay) || isSameDay(endTime, thisDay)
      )
      // Sort the shifts by start time
      .sort((a, b) => a.startTime - b.startTime),
  });

/**
 * The number of shifts in a set of divisions
 * @param divisions - The divisions to count the shifts of
 * @returns The number of shifts in the divisions
 */
const numberOfShifts = (
  divisions: Division | readonly Division[] | undefined
) =>
  [divisions ?? []]
    .flat()
    .flatMap(({ roles }) => roles.flatMap(({ shifts }) => shifts)).length;

/**
 * Takes two colours.
 * If the colours are equal, returns true.
 * If the colours are not equal, maps through the coloursThatCanBeStacked to see if the two colours
 * exist together in any tuples. If they do, it returns true, otherwise it returns false.
 */
const coloursCanBeStacked = (
  colour1: string | undefined,
  colour2: string | undefined
): boolean =>
  colour1 !== undefined &&
  colour2 !== undefined &&
  (colour1 === colour2 ||
    coloursThatCanBeStacked.some(
      ([colourA, colourB]) =>
        (colourA === colour1 && colourB === colour2) ||
        (colourA === colour2 && colourB === colour1)
    ));

/**
 * @react-pdf doesn't enable column breaking, so we have to stack columns manually.
 *
 * This takes an array of Divisions, and stacks them in a column if:
 *   i)  adjacent divisions are small enough (determined by maxShiftsPerColumn) AND
 *   ii) adjacent divisions have the same colour.
 *       - Note: the adjacency of colour determination is first & globally controlled by
 *         the order of the declaration of roles & color assignments within the `roleGroupPriorities`
 *         array. i.e. if groups are declared in `roleGroupPriorities` with colors ordered as
 *          [group1:red, group2:blue, group3:red]
 *         the two red groups would not be stacked.
 *
 * @param divisions - The divisions to stack
 * @returns A stack of divisions
 */
export const stackedDivisions = (
  divisions: readonly Division[]
): readonly (readonly Division[])[] =>
  divisions.reduce((stacks: readonly (readonly Division[])[], division) => {
    if (
      // If (Different colour)
      !coloursCanBeStacked(division.colour, stacks.at(-1)?.at(0)?.colour) ||
      // Or (Adding this division would create too many shifts in the stack)
      numberOfShifts(division) + numberOfShifts(stacks.at(-1)) >
        maxShiftsPerColumn
    ) {
      // Then (Start a new stack)
      return [...stacks, [division]];
    } else {
      // Else (Add to the current stack)
      return [...stacks.slice(0, -1), [...(stacks.at(-1) ?? []), division]];
    }
  }, []);

/**
 * Takes raw team roster data from the database and a date for which the roster should be generated,
 * and returns a roster object that can be used to generate a PDF.
 *
 * @param derivedTeam - The raw team roster data from the database
 * @param thisDay - The date for which the roster should be generated
 * @returns - A roster object that can be used to generate a PDF
 */
export const teamRosterThisDay = (
  derivedTeam: DerivedTeamRoster,
  thisDay: number
): Roster => ({
  name: derivedTeam.teamName,
  ion: derivedTeam.internalOrderNumber ?? 'N/A',
  ionDescription: derivedTeam.costObjectDescription ?? 'N/A',
  details: [
    `Team Type: ${derivedTeam.teamType}`,
    ...formatShiftTime(derivedTeam.dayShiftPeriod),
  ],
  topDivisions: groupRolesByDivision(
    derivedTeam.roleRosters
      .filter(({ roleName }) => isExecutive(roleName))
      .map((roster) => roleRosterToRole(roster)),
    thisDay
  ),
  bottomDivisions: groupRolesByDivision(
    // Disable immutability for array sorting
    // eslint-disable-next-line functional/immutable-data
    derivedTeam.roleRosters
      .filter(({ roleName }) => !isExecutive(roleName))
      .map((roster) => roleRosterToRole(roster))
      // Sort titles alphabetically
      .sort((a, b) => a.title.localeCompare(b.title))
      // Put the leaders first
      .sort((a, b) => (a.isLeader ? -1 : b.isLeader ? 1 : 0)),
    thisDay
  ),
});
